/**
 * Shared HTTP response handling — the one place every `*Api.ts` service
 * turns a `Response` into data or into a thrown error.
 *
 * This serves all 22 services that used to do their own: nineteen carried
 * a private `handleResponse<T>` copy, three more (plansApi, tenantsApi,
 * shopifyAuthApi) hand-rolled the same parse inline. They had drifted
 * apart in ways callers could feel:
 *
 *  - billingApi.ts / onboardingApi.ts unwrapped a structured
 *    `detail.message` AND `detail.code`; marketingSuiteApi.ts unwrapped
 *    `detail.message` only; the other sixteen copies did neither, so a
 *    structured FastAPI detail rendered as the literal "[object Object]".
 *  - dataUploadsApi.ts handled 204/205 and empty bodies; the rest called
 *    `response.json()` unconditionally, so a successful DELETE surfaced as
 *    "Unexpected end of JSON input" (PR #18).
 *  - agencyApi.ts / entitlementsApi.ts threw a bare `new Error(...)` with
 *    no `.status` at all, so callers branching on `err.status` silently
 *    saw `undefined`.
 *
 * The contract here is the union of the copies that got it right, and is
 * pinned by src/tests/httpResponse.test.ts.
 *
 * ON THE DECLARED RETURN TYPE: `Promise<T>`, not `Promise<T | undefined>`,
 * even though an empty/204/205 body genuinely resolves `undefined`. Nearly
 * every call site is `return handleResponse<X>(response)` inside a service
 * function declared `Promise<X>` — the exceptions are eleven bare sites
 * (eight in marketingSuiteApi, one each in dataUploadsApi, plansApi and
 * tenantsApi) that discard the result or infer T. Widening here would mean
 * either ~200 casts or changing the public signature of every service
 * (out of scope).
 * Endpoints that can legitimately answer with no content should be typed
 * `void` / `X | undefined` at the call site. This mirrors the convention
 * dataUploadsApi.ts already used and that its tests pin.
 */

/** Optional fields every API error can carry beyond its message. */
export interface ApiErrorOptions {
  /** Machine-readable error code, from `detail.code` or a top-level `code`. */
  code?: string;
  /** The response body's `detail` field, verbatim. Pages render it
   * directly (`err.detail || 'Please try again.'`), so this stays the
   * detail field rather than the whole envelope. */
  detail?: unknown;
}

/**
 * Transport-level API failure: an Error that always knows its HTTP status.
 *
 * Subclasses set their own `name` in their own constructor (repo
 * convention — deliberately not derived from `new.target`, so the name a
 * caller sees is the one written in the file they are reading). They must
 * NOT redeclare `status`/`code`/`detail`: with `useDefineForClassFields`
 * on, a redeclared field is defined as `undefined` after `super()` runs.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  detail?: unknown;

  constructor(message: string, status: number, options: ApiErrorOptions = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options.code;
    this.detail = options.detail;
  }
}

/**
 * Anything constructible with ApiError's shape. Lets a service keep its
 * own named error class (BillingApiError, OnboardingApiError) while still
 * routing every response through this module.
 */
export type ApiErrorConstructor = new (
  message: string,
  status: number,
  options?: ApiErrorOptions
) => ApiError;

export interface HandleResponseOptions {
  /** Message used when the body carries no usable detail. Defaults to one
   * that names the status, so an unlabelled failure is still diagnosable. */
  fallbackMessage?: string;
  /** Error class to throw instead of ApiError. */
  ErrorClass?: ApiErrorConstructor;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Read the body once and parse it best-effort. Never throws. */
async function readBody(
  response: Response
): Promise<{ text: string; parsed: unknown; valid: boolean }> {
  // The catch also swallows the "body already read" TypeError, i.e. a
  // genuine double-read bug, reporting it as an empty body. That is why
  // the test doubles hand out a FRESH Response per call rather than
  // reusing one via mockResolvedValue — a shared Response would look
  // like an empty 200 here instead of failing loudly.
  const text = await response.text().catch(() => '');
  if (!text.trim()) {
    return { text, parsed: undefined, valid: true };
  }
  try {
    return { text, parsed: JSON.parse(text), valid: true };
  } catch {
    return { text, parsed: undefined, valid: false };
  }
}

/** Whitespace-collapsed and capped at 200 characters: enough to tell an
 * HTML error page from a truncated payload in the console, without
 * dumping a whole document there. Console-only — never put the result on
 * a thrown error, which pages render. */
function snippet(text: string): string {
  const MAX = 200;
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed.length > MAX ? `${collapsed.slice(0, MAX - 1)}\u2026` : collapsed;
}

function normalizeOptions(
  options?: string | HandleResponseOptions
): HandleResponseOptions {
  return typeof options === 'string' ? { fallbackMessage: options } : options ?? {};
}

/**
 * Turn a `Response` into parsed data, or throw an error that carries the
 * status (and a code, when the body supplied one).
 *
 * Success: 204/205 and empty bodies resolve `undefined`; anything else is
 * parsed as JSON, and an unparseable *ok* body throws an ApiError rather
 * than leaking a raw SyntaxError.
 *
 * Failure: the message is taken from a string `detail`, then
 * `detail.message`, then `fallbackMessage`, then a default naming the
 * status.
 *
 * @param options A fallback message, or `{ fallbackMessage, ErrorClass }`.
 */
export async function handleResponse<T>(
  response: Response,
  options?: string | HandleResponseOptions
): Promise<T> {
  const { fallbackMessage, ErrorClass = ApiError } = normalizeOptions(options);

  if (!response.ok) {
    // Asymmetry with the ok path below, on purpose: an unparseable ERROR
    // body gets no snippet and no console.warn. A failure's message is
    // the one most likely to be rendered, non-JSON error bodies are
    // routine (gateway HTML, proxy notices), and warning on every 502
    // would be noise. The rarer unparseable-OK case is a real surprise
    // worth a console line.
    const { parsed } = await readBody(response);
    const body = isRecord(parsed) ? parsed : undefined;
    const detail = body?.detail;

    const detailMessage =
      typeof detail === 'string'
        ? detail
        : isRecord(detail) && typeof detail.message === 'string'
          ? detail.message
          : undefined;

    const detailCode =
      isRecord(detail) && typeof detail.code === 'string' ? detail.code : undefined;
    const topLevelCode = typeof body?.code === 'string' ? body.code : undefined;

    throw new ErrorClass(
      detailMessage || fallbackMessage || `API error: ${response.status}`,
      response.status,
      { code: detailCode ?? topLevelCode, detail }
    );
  }

  // No-content statuses have no body to read at all.
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  const { text, parsed, valid } = await readBody(response);
  if (!valid) {
    // NOTHING from the unparsed body reaches the thrown error. Pages
    // render both `err.detail || '<fallback>'` (six sites) and
    // `err.message` (~25 sites, e.g. Billing.tsx:120, Paywall.tsx:44,
    // Ask.tsx:63), and only plansApi passes a fallbackMessage — so a
    // snippet in either field would put raw server output (an HTML error
    // page, a proxy notice) into a user-facing banner. `.detail` stays
    // unset, exactly as when this path threw a raw SyntaxError, and the
    // message stays terse. The snippet goes to the console, where a
    // developer debugging "why is this endpoint not JSON" will find it.
    console.warn(
      `[handleResponse] HTTP ${response.status} body is not JSON: ${snippet(text)}`
    );
    throw new ErrorClass(
      fallbackMessage || `Invalid JSON in response body (HTTP ${response.status})`,
      response.status
    );
  }
  // An empty body on an ok response (a 200 with no Content-Length, a proxy
  // stripping the payload) is "nothing", not a parse failure.
  return parsed as T;
}
